import { useEffect, useRef, useState } from "preact/hooks";
import { api, type Folders, type Message, type SendingAlias } from "../api";
import { createForward, replyFields } from "../compose";
import { longDate, SYSTEM_NAMES, who } from "../format";
import { decode, formatSize, frameDocument, type Decoded } from "../mime";
import { Icon } from "./icons";
import { MessageRecipients } from "./MessageRecipients";
import { MessageSecurityLazy } from "./MessageSecurityLazy";

function MessageBody({ message, onDecoded, onError }: { message: Message; onDecoded: (decoded: Decoded) => void; onError: (error: string) => void }) {
  const [decoded, setDecoded] = useState<Decoded | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [images, setImages] = useState(false);
  const frame = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    let alive = true;
    api.raw(message.id)
      .then((raw) => decode(message.id, raw))
      .then((d) => {
        if (!alive) return;
        setDecoded(d);
        onDecoded(d);
        // Premier décodage : alimente l'extrait et la recherche plein texte côté serveur.
        if (message.snippet === null) api.index(message.id, d.text, d.attachments.length > 0).catch(() => {});
      })
      .catch((e) => {
        if (!alive) return;
        setError((e as Error).message);
        onError((e as Error).message);
      });
    return () => { alive = false; };
  }, [message.id, onDecoded, onError]);

  // Ajuste la hauteur du cadre à son contenu (le cadre n'exécute aucun script).
  const fit = () => {
    const doc = frame.current?.contentDocument;
    if (frame.current && doc?.documentElement) frame.current.style.height = `${doc.documentElement.scrollHeight}px`;
  };

  if (error) return <div class="notice">Impossible d'afficher cet e-mail : {error}</div>;
  if (!decoded) return <div class="notice">Chargement…</div>;
  return (
    <>
      {decoded.hasRemoteImages && !images && (
        <div class="notice">
          Les images distantes sont bloquées pour protéger votre vie privée.
          <button onClick={() => setImages(true)}>Afficher les images</button>
        </div>
      )}
      {decoded.html ? (
        <iframe
          ref={frame} class="mail-frame" title={message.subject ?? "E-mail"}
          // Aucun script : seuls l'accès au contenu (pour la hauteur) et l'ouverture des liens
          // dans un nouvel onglet sont permis.
          sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          referrerpolicy="no-referrer"
          srcdoc={frameDocument(decoded.html, images)}
          onLoad={fit}
        />
      ) : (
        <pre class="mail-text">{decoded.text}</pre>
      )}
      {decoded.attachments.length > 0 && (
        <div class="attachments">
          {decoded.attachments.map((a) => (
            <a class="attachment" href={a.url} download={a.filename} target="_blank" rel="noopener">
              {a.mimeType.startsWith("image/") && !/svg/i.test(a.mimeType) && <img src={a.url} alt="" />}
              <span class="name">{a.filename}</span>
              <span class="size">{formatSize(a.size)}</span>
            </a>
          ))}
        </div>
      )}
    </>
  );
}

// Envoie une copie de l'e-mail à Gmail ; une seconde copie demande confirmation.
function ForwardButton({ message, notify }: { message: Message; notify: (text: string) => void }) {
  const [forwardedAt, setForwardedAt] = useState(message.forwardedAt);
  const [busy, setBusy] = useState(false);
  const forward = async () => {
    if (forwardedAt && !confirm(`Déjà transféré vers Gmail le ${longDate(forwardedAt)}. Le transférer à nouveau ?`)) return;
    setBusy(true);
    try {
      const res = await api.forward(message.id);
      setForwardedAt(res.forwardedAt);
      notify("Transféré vers Gmail");
    } catch (e) {
      notify((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button class="chip" onClick={forward} disabled={busy}>
      <Icon name="forward" size={16} />
      {busy ? "Transfert…" : forwardedAt ? `Transféré vers Gmail · ${longDate(forwardedAt)}` : "Transférer vers Gmail"}
    </button>
  );
}

// Répondre, répondre à tous, transférer : crée le brouillon puis ouvre l'éditeur.
function ComposeButtons({ message, aliases, onCompose, notify }: {
  message: Message;
  aliases: SendingAlias[];
  onCompose: (draftId: string) => void;
  notify: (text: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const others = [...message.to, ...message.cc].filter((m) => !m.address.endsWith("@robinluiz.fr") && !aliases.some((a) => a.alias === m.address));
  const run = async (mode: "reply" | "all" | "forward") => {
    setBusy(true);
    try {
      const decoded = await decode(message.id, await api.raw(message.id));
      const draft = mode === "forward" ? await createForward(message, decoded) : await api.createDraft(replyFields(message, decoded, mode === "all", aliases));
      onCompose(draft.id);
    } catch (e) {
      notify((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button class="chip" onClick={() => run("reply")} disabled={busy}><Icon name="reply" size={16} />Répondre</button>
      {others.length > (message.location === "sent" ? 1 : 0) && (
        <button class="chip" onClick={() => run("all")} disabled={busy}><Icon name="replyAll" size={16} />Répondre à tous</button>
      )}
      <button class="chip" onClick={() => run("forward")} disabled={busy}><Icon name="forward" size={16} />Transférer</button>
    </>
  );
}

function MessageItem({ message, aliases, open: initiallyOpen, notify, onCompose, onAsk, onSettings }: {
  message: Message;
  aliases: SendingAlias[];
  open: boolean;
  notify: (text: string) => void;
  onCompose: (draftId: string) => void;
  onAsk: (messageId: string) => void;
  onSettings: () => void;
}) {
  const [open, setOpen] = useState(initiallyOpen);
  const [decoded, setDecoded] = useState<Decoded | null>(null);
  const [decodeError, setDecodeError] = useState<string | null>(null);
  const [securityOpen, setSecurityOpen] = useState(false);
  const securityButton = useRef<HTMLButtonElement>(null);
  return (
    <article class={`message${open ? "" : " collapsed"}`}>
      <header class="message-head" onClick={() => !open && setOpen(true)}>
        <span class="from">{who(message.from)}</span>
        {message.from.name && <span class="addr">{message.from.address}</span>}
        <span class="date">{longDate(message.receivedAt)}</span>
        {open && <MessageRecipients message={message} decoded={decoded?.recipients ?? null} />}
      </header>
      {open && message.auth === "fail" && (
        <div class="notice warn" role="alert">
          ⚠ Expéditeur non authentifié : rien ne prouve que cet e-mail vient vraiment de
          « {message.from.address.split("@")[1]} ». Méfiez-vous de ses liens et de ses pièces jointes.
        </div>
      )}
      {open && (
        <div class="message-actions">
          <ComposeButtons message={message} aliases={aliases} onCompose={onCompose} notify={notify} />
          <ForwardButton message={message} notify={notify} />
          <button ref={securityButton} class="chip" aria-expanded={securityOpen} aria-controls={"security-" + message.id} onClick={() => setSecurityOpen(!securityOpen)}>
            <Icon name="shield" size={16} />Analyse de sécurité
          </button>
          {message.location !== "sent" && (
            <button class="chip" onClick={() => onAsk(message.id)}><Icon name="spark" size={16} />Demander à l'IA</button>
          )}
        </div>
      )}
      {open && securityOpen && <MessageSecurityLazy message={message} decoded={decoded} error={decodeError} onSettings={onSettings} onAsk={() => onAsk(message.id)} onClose={() => { setSecurityOpen(false); securityButton.current?.focus(); }} />}
      {open ? <MessageBody message={message} onDecoded={setDecoded} onError={setDecodeError} /> : <div class="preview">{message.snippet ?? ""}</div>}
    </article>
  );
}

export function Reader({ threadId, location, folders, onBack, onChanged, onCompose, onAsk, onSettings }: {
  threadId: string;
  location: string;
  folders: Folders | null;
  onBack: () => void;
  onChanged: (message: string, undo?: () => Promise<void>) => void;
  onCompose: (draftId: string) => void;
  onAsk: (messageId: string) => void;
  onSettings: () => void;
}) {
  const [messages, setMessages] = useState<Message[] | null>(null);
  const [menu, setMenu] = useState(false);

  useEffect(() => {
    setMessages(null);
    setMenu(false);
    api.thread(threadId, location).then(({ messages }) => {
      setMessages(messages);
      const unread = messages.filter((m) => !m.read).map((m) => m.id);
      // Hors ligne, l'e-mail reste non lu : il le sera à la prochaine ouverture.
      if (unread.length) api.update(unread, { read: true }).then(() => onChanged("")).catch(() => {});
    }).catch(() => setMessages([]));
  }, [threadId, location]);

  if (!messages) return <section class="reader"><div class="placeholder">Chargement…</div></section>;
  if (messages.length === 0) return <section class="reader"><div class="placeholder">Conversation introuvable.</div></section>;

  // Ranger une conversation ne touche pas aux copies de « Envoyés », sauf depuis ce dossier.
  const here = location;
  const own = messages.filter((m) => (here === "sent") === (m.location === "sent"));
  const ids = (own.length ? own : messages).map((m) => m.id);
  const starred = messages.some((m) => m.starred);

  const move = async (to: string, label: string) => {
    const previous = messages.filter((m) => ids.includes(m.id)).map((m) => ({ id: m.id, location: m.location }));
    await api.update(ids, { location: to });
    onChanged(label, async () => {
      for (const loc of new Set(previous.map((p) => p.location))) {
        await api.update(previous.filter((p) => p.location === loc).map((p) => p.id), { location: loc });
      }
    });
    onBack();
  };
  const toggleStar = async () => {
    await api.update(ids, { starred: !starred });
    setMessages(messages.map((m) => ({ ...m, starred: !starred })));
    onChanged("");
  };
  const markUnread = async () => {
    await api.update([ids[ids.length - 1]], { read: false });
    onChanged("Marquée comme non lue");
    onBack();
  };
  const destroy = async () => {
    if (!confirm("Supprimer définitivement cette conversation ? Cette action est irréversible.")) return;
    await api.deleteForever(ids);
    onChanged("Conversation supprimée définitivement");
    onBack();
  };

  const destinations = [
    { id: "inbox", name: "Boîte de réception" },
    ...(folders?.custom ?? []).map((f) => ({ id: f.id, name: f.name })),
    { id: "archive", name: SYSTEM_NAMES.archive },
    { id: "spam", name: SYSTEM_NAMES.spam },
  ].filter((d) => d.id !== here);

  return (
    <section class="reader">
      <div class="reader-head">
        <button class="icon-btn only-mobile" aria-label="Retour" onClick={onBack}><Icon name="back" /></button>
        {here === "trash" ? (
          <>
            <button class="icon-btn" title="Restaurer" aria-label="Restaurer" onClick={() => move("inbox", "Conversation restaurée")}><Icon name="restore" /></button>
            <button class="btn danger" onClick={destroy}>Supprimer définitivement</button>
          </>
        ) : (
          <>
            {here !== "archive" && <button class="icon-btn" title="Archiver" aria-label="Archiver" onClick={() => move("archive", "Conversation archivée")}><Icon name="archive" /></button>}
            <button class="icon-btn" title="Supprimer" aria-label="Supprimer" onClick={() => move("trash", "Conversation mise à la corbeille")}><Icon name="trash" /></button>
          </>
        )}
        <div style="position: relative">
          <button class="icon-btn" title="Déplacer" aria-label="Déplacer vers…" aria-expanded={menu} onClick={() => setMenu(!menu)}><Icon name="move" /></button>
          {menu && (
            <div class="menu" role="menu">
              {destinations.map((d) => (
                <button role="menuitem" onClick={() => move(d.id, `Déplacée vers « ${d.name} »`)}>{d.name}</button>
              ))}
            </div>
          )}
        </div>
        <span class="spacer" />
        <button class="icon-btn" title="Marquer comme non lue" aria-label="Marquer comme non lue" onClick={markUnread}><Icon name="unread" /></button>
        <button class="icon-btn" title={starred ? "Retirer l'étoile" : "Ajouter une étoile"} aria-pressed={starred} onClick={toggleStar}>
          <span class={starred ? "star" : ""}><Icon name="star" filled={starred} /></span>
        </button>
      </div>
      <div class="reader-body">
        <h1>{messages[0].subject || "(sans objet)"}</h1>
        {messages.map((m, i) => <MessageItem key={m.id} message={m} aliases={folders?.aliases ?? []} open={i === messages.length - 1 || !m.read} notify={(text) => onChanged(text)} onCompose={onCompose} onAsk={onAsk} onSettings={onSettings} />)}
      </div>
    </section>
  );
}
